/**
 * Debug Overlay
 * Contains functions for creating and updating the debug overlay
 */

/**
 * Create a debug overlay to visualize UTM-tagged links
 * 
 * @param {Object} options Options for creating the debug overlay
 * @param {string} options.currentHostname Current hostname
 * @param {string} options.currentPathname Current pathname
 * @param {string} options.utmMedium utm_medium value
 * @param {string} options.utmCampaign utm_campaign value
 * @param {Object} options.i18n Internationalization strings
 * @return {Object} The debug overlay elements
 */
export function createDebugOverlay(options) {
    const { currentHostname, currentPathname, utmMedium, utmCampaign, i18n = {} } = options;

    // Create the debug overlay container. currentHostname/currentPathname
    // (window.location), utmMedium/utmCampaign (derived from the current
    // path), and every i18n.* string below are written via createElement +
    // textContent, never interpolated into an HTML string — same
    // anti-pattern class as updateDebugList() (debugMode && isAdmin only,
    // but the sink itself must not be exploitable). Class names and DOM
    // structure are unchanged so updateDebugList(), the toggle wiring below,
    // and the plugin's CSS all keep working.
    const overlay = document.createElement('div');
    overlay.className = 'utm-debug-overlay';

    const header = document.createElement('div');
    header.className = 'utm-debug-header';

    const title = document.createElement('h3');
    title.textContent = i18n.debugTitle || 'UTM Linker Debug';

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'utm-debug-toggle';
    toggleBtn.textContent = i18n.hideButton || 'Hide';

    header.appendChild(title);
    header.appendChild(toggleBtn);

    const content = document.createElement('div');
    content.className = 'utm-debug-content';

    const summary = document.createElement('div');
    summary.className = 'utm-debug-summary';
    summary.appendChild(createSummaryRow(i18n.hostname || 'Hostname', currentHostname));
    summary.appendChild(createSummaryRow(i18n.path || 'Path', currentPathname));
    summary.appendChild(createSummaryRow(i18n.utmMedium || 'UTM Medium', utmMedium));
    summary.appendChild(createSummaryRow(i18n.utmCampaign || 'UTM Campaign', utmCampaign));

    const linksSection = document.createElement('div');
    linksSection.className = 'utm-debug-links';

    const linksHeading = document.createElement('h4');
    linksHeading.textContent = i18n.processedLinks || 'Processed Links';

    const debugListEl = document.createElement('ul');
    debugListEl.className = 'utm-debug-list';

    linksSection.appendChild(linksHeading);
    linksSection.appendChild(debugListEl);

    content.appendChild(summary);
    content.appendChild(linksSection);

    overlay.appendChild(header);
    overlay.appendChild(content);

    // Add the overlay to the page
    document.body.appendChild(overlay);

    // Add toggle functionality
    toggleBtn.addEventListener('click', () => {
        if (content.style.display === 'none') {
            content.style.display = 'block';
            toggleBtn.textContent = i18n.hideButton || 'Hide';
        } else {
            content.style.display = 'none';
            toggleBtn.textContent = i18n.showButton || 'Show';
        }
    });

    // Return references to overlay elements
    return {
        overlay,
        debugList: overlay.querySelector('.utm-debug-list'),
        toggleBtn,
        content
    };
}

/**
 * Build a single "<label>: <value>" summary row
 * (`<p>label: <strong>value</strong></p>`) with both label and value
 * injected via text nodes, never HTML interpolation.
 *
 * @param {string} label The row label (already resolved from i18n or a default)
 * @param {string} value The dynamic value (currentHostname/currentPathname/utmMedium/utmCampaign)
 * @return {HTMLParagraphElement}
 */
function createSummaryRow(label, value) {
    const p = document.createElement('p');
    p.appendChild(document.createTextNode(label + ': '));
    const strong = document.createElement('strong');
    strong.textContent = value;
    p.appendChild(strong);
    return p;
}

/**
 * Update the debug overlay list with processed links
 * 
 * @param {HTMLElement} debugList The debug list element to update
 * @param {Array} processedLinks Array of processed link data
 * @param {Object} i18n Internationalization strings
 */
export function updateDebugList(debugList, processedLinks, i18n = {}) {
    if (!debugList) return;

    // Clear the list
    debugList.innerHTML = '';

    // Add each processed link to the list. Every untrusted value below
    // (linkText comes from link.element.textContent, which decodes HTML
    // entities to raw characters; newUrl and utmParams values are derived
    // from page content too) is written via textContent/createTextNode, never
    // interpolated into an HTML string — an anchor's text or a query value
    // that looks like markup must stay inert text, not become live DOM
    // (debugMode && isAdmin only, but the sink itself must not be exploitable).
    processedLinks.forEach((link, index) => {
        const item = document.createElement('li');

        const linkText = link.element.textContent.trim() ||
                        (link.element.querySelector('img') ?
                         (i18n.imageLink || 'Image link') :
                         (i18n.link || 'Link') + ' ' + (index + 1));

        const titleDiv = document.createElement('div');
        titleDiv.className = 'utm-debug-link-title';
        titleDiv.textContent = linkText;

        const urlDiv = document.createElement('div');
        urlDiv.className = 'utm-debug-link-url';
        const urlSmall = document.createElement('small');
        urlSmall.textContent = link.newUrl;
        urlDiv.appendChild(urlSmall);

        const paramsDiv = document.createElement('div');
        paramsDiv.className = 'utm-debug-link-params';
        for (const [key, value] of Object.entries(link.utmParams)) {
            paramsDiv.appendChild(document.createElement('br'));
            const strong = document.createElement('strong');
            strong.textContent = key;
            paramsDiv.appendChild(strong);
            paramsDiv.appendChild(document.createTextNode(': ' + value));
        }

        item.appendChild(titleDiv);
        item.appendChild(urlDiv);
        item.appendChild(paramsDiv);

        debugList.appendChild(item);
    });
}

/**
 * Mark a processed link for debugging with a visual indicator
 * 
 * @param {HTMLAnchorElement} link The link element
 * @param {string} status The processing status
 * @param {Object|string} utmParams UTM parameters or status message
 */
export function markProcessedLink(link, status, utmParams) {
    link.classList.add('utm-processed', `utm-${status}`);
    
    // Add a data attribute with the UTM params for the debug overlay
    if (typeof utmParams === 'object') {
        link.setAttribute('data-utm-params', JSON.stringify(utmParams));
    } else {
        link.setAttribute('data-utm-status', utmParams);
    }
}

/**
 * Log debug information to the console
 *
 * @param {Object} options Debug options
 * @param {Array} options.excludeDomains Excluded domains
 */
export function logDebugInfo(options) {
    const { excludeDomains } = options;

    console.log('=== UTM Linker Debug Info ===');

    // Log exclude domains info
    console.log('• Excluded domains:', excludeDomains);

    if (Array.isArray(excludeDomains)) {
        console.log('  - Excluded domains count:', excludeDomains.length);
        if (excludeDomains.length > 0) {
            console.log('  - First 5 domains:', excludeDomains.slice(0, 5));
        }
    }

    console.log('===========================');
}